<template>
  <b-dropdown
    :id="`${id}-dropdown`"
    v-click-outside="hideDrop"
    :text="text"
    block
    variant="none"
    :class="customDropdownClasses"
    menu-class="w-100 custom-select-menu"
    toggle-class="p-0"
    toggle-tag="div"
    dropdown
    no-caret
    @show.prevent
    @hide.prevent
  >
    <template #button-content>
      <div
        class="select-wrapper"
        @click="handleClick"
      >
        <search-icon
          v-if="displayPrefixIcon && !!searchPlaceholder"
          class="search-icon"
        />
        <feather-icon
          v-if="showArrowIcon"
          class="arrow-icon"
          :class="{ 'arrow-icon-up': showDropdown }"
          icon="ChevronDownIcon"
          size="16"
          @click.stop="handleClick"
        />
        <v-select
          :value="value"
          :options="allOptions"
          :input-id="id"
          multiple
          :deselect-from-dropdown="true"
          :class="selectClasses"
          :no-drop="true"
          :searchable="isSearchable"
          :placeholder="searchPlaceholder"
          class="dropdown"
          :map-keydown="handleKeyDown"
          @input="handleSelectInput"
          @search="onSearch"
          @search:focus="handleSearchFocus(true)"
          @search:blur="handleSearchFocus(false)"
          @option:deselected="handleOptionDeselect"
        />
      </div>
    </template>
    <template v-for="(optionGroup, index) in optionGroups">
      <b-dropdown-header
        v-if="optionGroup.title && optionGroup.options && optionGroup.options.length"
        :key="`${index}_${optionGroup.title}`"
        class="country-header"
        @click="toggleGroup(index, $event)"
      >
        <div class="d-flex justify-content-between align-items-center from-map-1">
          <span>{{ optionGroup.title }}</span>
          <span
            v-if="isGroupExpandable"
            :class="{ 'arrow-icon-up': !isGroupExpandable || expandedGroups.includes(index) }"
          >
            <arrow-down-icon />
          </span>
        </div>
      </b-dropdown-header>
      <template v-if="!isGroupExpandable || expandedGroups.includes(index)">
        <template v-if="optionGroup.options.length > 0 && typeof optionGroup.options[0] === 'object'">
          <template v-for="(industryGroup, industryIndex) in optionGroup.options">
            <b-dropdown-header
              v-if="industryGroup.industry"
              :key="`optionGroup_${optionGroup.title}_industry_${industryGroup.industry}_${industryIndex}`"
              class="industry-header"
            >
              {{ industryGroup.industry }}
            </b-dropdown-header>
            <b-dropdown-item
              v-for="(option, optionIndex) in industryGroup.options"
              :key="`kc_optionGroup_${optionGroup.title}_option_${option}_${optionIndex}_${industryGroup.industry}_${industryIndex}`"
              :active="value.includes(option)"
              @click.stop="onSelect(option)"
            >
              <div class="d-flex w-100 justify-content-between align-items-center from-map-2">
                <span class="w-100 text-wrap">{{ option }}</span>
                <tick-icon v-if="value.includes(option)" />
              </div>
            </b-dropdown-item>
          </template>
        </template>
        <template v-else>
          <b-dropdown-item
            v-for="(option, optionIndex) in optionGroup.options"
            :key="`kc_optionGroup_${optionGroup.title}_option_${option}_${optionIndex}`"
            :active="value.includes(option)"
            @click.stop="onSelect(option)"
          >
            <div class="d-flex w-100 justify-content-between align-items-center">
              <span class="w-100 text-wrap">{{ option }}</span>
              <tick-icon v-if="value.includes(option)" />
            </div>
          </b-dropdown-item>
        </template>
      </template>
    </template>

    <template v-if="!allOptions.length && !addNewOption">
      <b-dropdown-item
        :disabled="true"
      >
        <div class="d-flex w-100 justify-content-between align-items-center">
          {{ noDataText }}
        </div>
      </b-dropdown-item>
    </template>
    <b-dropdown-item
      v-if="addNewOption && localSearchInput"
      @click.stop="onSelect(localSearchInput)"
    >
      <span class="w-100 text-wrap">{{ localSearchInput }}</span>
    </b-dropdown-item>
  </b-dropdown>
</template>
<script>
import {
  BDropdown,
  BDropdownHeader,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import TickIcon from '@/@core/assets/svg-components/TickIcon.vue'
import SearchIcon from '@/@core/assets/svg-components/SearchIcon.vue'
import ArrowDownIcon from '@/@core/assets/svg-components/ArrowIcon.vue'

export default {
  name: 'CustomSelect',
  components: {
    BDropdown,
    BDropdownItem,
    vSelect,
    TickIcon,
    SearchIcon,
    BDropdownHeader,
    ArrowDownIcon,
  },
  emits: ['input', 'search', 'select', 'focus', 'blur', 'custom-item'],
  props: {
    optionGroups: {
      type: Array,
      default: () => [],
      required: true,
    },
    addNewOption: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    value: {
      type: Array,
      default: () => [],
      required: true,
    },
    theme: {
      type: String,
      default: 'black',
    },
    displayPrefixIcon: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearSearchOnSelect: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: 'No data found.',
    },
    id: {
      type: String,
      required: true,
      default: 'searchDropdown',
    },
    showArrowIcon: {
      type: Boolean,
      default: false,
    },
    isSearchable: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'large',
    },
    shouldScrollOnFocus: {
      type: Boolean,
      default: false,
    },
    isGroupExpandable: {
      type: Boolean,
      default: false,
    },
    isSelectionRemovable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDropdown: false,
      localSearchInput: '',
      expandedGroups: [],
    }
  },
  computed: {
    searchPlaceholder() {
      return !this.showDropdown && !this.value?.length ? this.placeholder : ''
    },
    allOptions() {
      const allOptions = []
      this.optionGroups?.forEach(optionGroup => {
        if (optionGroup.options.length > 0 && typeof optionGroup.options[0] === 'object') {
          optionGroup.options.forEach(industryGroup => {
            allOptions.push(...industryGroup.options)
          })
        } else {
          allOptions.push(...optionGroup.options)
        }
      })
      return allOptions
    },
    customDropdownClasses() {
      return {
        show: this.showDropdown,
        'custom-select-dropdown-grey': this.theme === 'grey',
        'custom-select-dropdown-black': this.theme === 'black',
        'small-size': this.size === 'small',
        'is-invalid': this.invalid,
        disabled: this.disabled,
      }
    },
    selectClasses() {
      return {
        inputLeftPadding: this.displayPrefixIcon && !!this.searchPlaceholder,
        deselectDisabled: !this.isSelectionRemovable,
        disabled: this.disabled,
      }
    },
  },
  watch: {
    showDropdown(val) {
      if (val && this.shouldScrollOnFocus) {
        setTimeout(() => {
          const dropdownEl = document.querySelector(`#${this.id}-dropdown > .custom-select-menu`)
          const list = dropdownEl.parentElement
          if (!this.isElementInViewport(list)) {
            window.scrollBy({
              top: list.clientHeight,
              behavior: 'smooth',
            })
          }
        }, 100)
      }
    },
  },
  mounted() {
    if (!this.isSearchable) {
      const inputElement = document.getElementById(this.id)
      if (inputElement) {
        inputElement.classList.add('non-searchable-input')
      }
    }
  },
  methods: {
    isElementInViewport(el) {
      const {
        top, left, bottom, right,
      } = el.getBoundingClientRect()
      const { clientHeight, clientWidth } = document.documentElement
      return top >= 0 && left >= 0 && bottom <= clientHeight && right <= clientWidth
    },
    checkInputWidth(inputElement) {
      const inputWidth = inputElement.offsetWidth
      if (inputWidth < 80) {
        inputElement.classList.add('min-width')
      }
    },
    handleClick(e) {
      if (!this.isValidTargetElement(e.target)) {
        return
      }

      const dropdownIsOpen = this.showDropdown
      const inputHasValue = this.hasInputValue()
      if (dropdownIsOpen && inputHasValue) {
        e.stopPropagation()
        return
      }

      this.toggleDropdown()
    },
    handleClickCustomItem() {
      this.$emit('custom-item', this.localSearchInput)
      if (this.closeOnSelect) {
        this.hideDrop()
      }
      if (this.clearSearchOnSelect) {
        this.resetSearch()
      } else this.focusSearchInput()
    },
    handleSearchFocus(isFocused) {
      if (isFocused) {
        this.$emit('focus')
      } else {
        this.$emit('blur')
      }
    },
    isValidTargetElement(target) {
      const allowedTags = ['input', 'div', 'span', 'svg', 'polyline', 'path']
      return allowedTags.includes(target.tagName.toLowerCase())
    },
    hasInputValue() {
      return this.searchText ? this.searchText.trim() !== '' : false
    },
    toggleDropdown() {
      if (this.showDropdown) {
        document.activeElement?.blur()
      }
      this.showDropdown = !this.showDropdown
    },
    onSelect(selectedOption) {
      let updatedOptions = [...this.value]
      if (updatedOptions.includes(selectedOption) && this.isSelectionRemovable) {
        updatedOptions = updatedOptions.filter(option => option !== selectedOption)
      } else if (this.multiple) {
        updatedOptions.push(selectedOption)
      } else {
        updatedOptions = [selectedOption]
      }
      this.$emit('select', updatedOptions)
      if (this.closeOnSelect) {
        this.hideDrop()
      }
      if (this.clearSearchOnSelect) {
        this.resetSearch()
      } else this.focusSearchInput()
    },
    onSearch(searchString) {
      this.localSearchInput = searchString
      if (searchString) {
        this.showDropdown = true
      }
      this.$emit('search', searchString)
      if (searchString) {
        this.expandedGroups = this.optionGroups
          .map((group, index) => {
            const hasMatchingOptions = group.options.some(industryGroup => {
              if (typeof industryGroup === 'object' && industryGroup.options) {
                return industryGroup.options.some(option => option.toLowerCase().includes(searchString.toLowerCase()))
              }
              return industryGroup.toLowerCase().includes(searchString.toLowerCase())
            })
            return hasMatchingOptions ? index : null
          })
          .filter(index => index !== null)

        if (this.isGroupExpandable) {
          this.expandedGroups = this.optionGroups.map((_, index) => index)
        }
      } else {
        this.expandedGroups = []
      }
    },
    clickOutside(event) {
      if (!this.showDropdown) return
      const target = event.target
      if (!target.closest(`#${this.id}-dropdown`)) {
        this.hideDrop()
      }
    },
    hideDrop() {
      this.showDropdown = false
      const el = document.getElementById(this.id)
      if (el) {
        el.blur()
      }
      const dropdownMenu = document.getElementsByClassName('custom-select-menu')
      if (dropdownMenu?.length) {
        dropdownMenu[0].scrollTop = 0
      }
    },
    handleSelectInput(val) {
      this.$emit('input', val)
    },
    resetSearch() {
      this.localSearchInput = ''
      const inputElement = document.getElementById(this.id)
      if (inputElement) {
        // Check input width, if it is less than 80px, add min-width class
        // to ensure that user views the input field correctly
        setTimeout(() => {
          this.checkInputWidth(inputElement)
        }, 100)
        inputElement.blur()
        inputElement.value = ''
        inputElement.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Escape',
            keyCode: 27,
            code: 'Escape',
            which: 27,
        }))
      }
      this.$emit('search', '')
    },
    handleOptionDeselect(option) {
      const updatedOptions = this.value.filter(o => o !== option)
      this.$emit('select', updatedOptions)
      if (updatedOptions.length > 0) {
        return
      }
      this.resetSearch()
      this.hideDrop()
    },
    focusSearchInput() {
      const inputElement = document.getElementById(this.id)
      if (inputElement) {
        inputElement?.focus()
      }
    },
    handleKeyDown(map, vm) {
      return {
        ...map,
        13: e => {
          e.preventDefault()
        },
        32: () => {
          if (vm.search) {
            vm.search += ' '
          }
        },
      }
    },
    toggleGroup(index, event) {
      if (event) {
        event.stopPropagation()
      }
      if (this.isGroupExpandable) {
        if (this.expandedGroups.includes(index)) {
          this.expandedGroups = this.expandedGroups.filter(i => i !== index)
        } else {
          this.expandedGroups.push(index)
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include'; // Bootstrap includes

.disabled {
  cursor: not-allowed;
  pointer-events: none;
  .vs__dropdown-toggle {
    background-color: #F7F7F7;
  }
  .vs__selected {
    color: #B9B9B8;
  }
}
.inputLeftPadding {
  input {
    padding-left: 35px;
  }
}

.deselectDisabled {
  div.vs__selected-options > span > button {
    display: none;
  }
}

.custom-select-dropdown-black {
  .dropdown-menu {
    max-height: 300px !important;
    z-index: 20;
    overflow-x: auto;
    .country-header {
      header {
        color: $primary !important;
        font-weight: 1000 !important;
      }
      background-color: $gray-600 !important;
    }
  }
}
.dropdown .vs__selected-options {
  max-height: 91px;
  overflow: auto;
}
.custom-select-dropdown-grey {
  &.dropdown.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545 !important;
    }
  }
  .dropdown-menu {
    z-index: 20;
  }
  &.dropdown {
    border: none !important;
    height: unset !important;
    .vs__dropdown-toggle {
      border-color: #E0E0E0;
    }
    .vs__selected {
      overflow: auto;
      background-color: #EBEAE8 !important;
      color: #000 !important;
      max-width: 90%;
      svg {
        color: #000 !important;
      }
    }
    .vs__actions {
      display: none;
    }

    .vs__selected-options {
      padding-left: 9px;
    }
  }
  .select-wrapper {
    position: relative;
  }
  .search-icon {
    position: absolute;
    left: 15px;
    top: 11px;
  }
  .arrow-icon {
    position: absolute;
    right: 9px;
    top: 11px;
    cursor: pointer;
    z-index: 2;
  }
  .arrow-icon-up {
    transform: rotate(180deg);
  }
  .non-searchable-input {
    min-width: unset !important;
    padding-right: unset !important;
  }
  .min-width {
    min-width: 80px !important;
  }
  input {
    margin-top: 3px;
    margin-bottom: 1px;
    &::placeholder {
      color: #757575
    }
  }
  input:placeholder-shown {
    text-overflow: ellipsis;
  }
  input.vs__search {
    margin: 4px 20px 0 0 !important;
  }
  .dropdown-menu-header-inverted {
    .country-header {
      svg {
        transform: rotate(180deg);
      }
    }
  }
  .dropdown-menu {
    max-height: 300px !important;
    overflow-x: auto;

    .country-header {
      header {
        color: $primary !important;
        font-weight: 1000 !important;
      }
      background-color: $gray-600 !important;
    }
    .industry-header {
      background-color: #f0f0f0;
      font-weight: bold;
      padding-left: 1rem;
    }
    li .dropdown-item {
      z-index: 3;
      &.active, &:focus {
        background-color: #EBEAE8;
        color: $primary
      }
    }

    li .custom-select-item {
      a {
        width: 100% !important;
      }
    }
  }
  .btn {
    border: none;
  }
}

.small-size {
  &.dropdown {
    padding-bottom: 1px;
    .vs__dropdown-toggle {
      border-color: #E0E0E0;
      min-height: 38px;
    }
    .dropdown-item {
      font-size: 12px;
      padding: 0.65rem;

      a {
        width: 100%;
      }
    }
    .vs__selected-options {
      padding-left: 4px;
    }

    .vs__selected {
      font-size: 12px;
    }
  }
  input {
    padding-bottom: 1px;
    margin-top: 5px;
    font-size: 12px !important;
  }
}

</style>
